"use client";

import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { isValidElement, type ReactNode } from "react";

interface FetchSuggestionPillProps {
  /** A Lucide icon component rendered at this pill's own default size, or a pre-rendered node
   *  (e.g. an `Avatar`) for a leading glyph whose sizing this component doesn't own. */
  icon: LucideIcon | ReactNode;
  label: string;
  onClick: () => void;
  /** Removes the pill from the tab order and blocks the click, e.g. while a prior pick is still
   *  navigating. Defaults to false (existing behavior). */
  disabled?: boolean;
}

/** Above this length a label reliably wraps to 2+ lines at the sidebar's width — past that, a full
 *  `rounded-full` pill radius reads as a stretched oval rather than a rounded rectangle, so the
 *  shape switches to a smaller-radius card instead. */
const LONG_LABEL_CHARS = 40;

/**
 * Clickable starter prompt for an empty chat/assistant state. Indigo-tinted neutral that warms
 * on hover. Renders as a pill for a short, single-line label (e.g. "Add a FAQ section") and as a
 * top-aligned card for a longer, multi-line one (e.g. an AI-generated, event-grounded prompt) —
 * a pill's fully rounded corners look stretched once the label wraps past one line. Promoted from
 * `enterprise-chat`'s local `SuggestionPill` — shared by more than one feature (enterprise-chat's
 * own initial state, the landing-page-v2 copilot), so it belongs here rather than in either
 * feature folder.
 */
export const FetchSuggestionPill = ({ icon, label, onClick, disabled = false }: FetchSuggestionPillProps) => {
  const isLong = label.length > LONG_LABEL_CHARS;
  // A pre-rendered node (e.g. `<Avatar />`) is a real React element; a Lucide icon is a component
  // *type* to invoke as `<Icon />` — but most Lucide icons are `forwardRef` objects, not plain
  // functions, so `typeof icon === "function"` misses them and tries to render the component
  // object itself as a child. `isValidElement` is the reliable way to tell "already an element"
  // from "a type that still needs to be invoked", regardless of which kind of component type it is.
  const Icon = isValidElement(icon) ? null : (icon as LucideIcon);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      transition={{
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
      }}
      className={`group flex w-full min-h-[44px] gap-2.5 border border-gray-200 bg-white text-left transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
        isLong ? "items-start rounded-2xl px-4 py-3" : "items-center rounded-full px-4 py-2.5"
      }`}
    >
      {Icon ? (
        <Icon
          className={`size-4 shrink-0 text-gray-500 transition-transform group-hover:scale-110 ${isLong ? "mt-0.5" : ""}`}
        />
      ) : (
        <span aria-hidden="true" className={`flex shrink-0 ${isLong ? "mt-0.5" : ""}`}>
          {icon as ReactNode}
        </span>
      )}
      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{label}</span>
    </motion.button>
  );
};
